import { FC, HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutsideAlerter } from "hooks/useOutsideAlerter";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { ReactComponent as TickIcon } from "images/TickIcon.svg";
import { SelectObjectListItem } from "./SelectObjectListItem/SelectObjectListItem";
import { ChangeChooseAll } from "toolkit/steps/stepThree/stepThree.slice";
import styles from "./SelectObjectList.module.sass";
import cn from "classnames";
import { declOfLessonsNum, trimString } from "helpers";

interface SelectObjectListProps extends HTMLAttributes<HTMLDivElement> {
	condition: boolean;
	mode: string;
}

export const SelectObjectList: FC<SelectObjectListProps> = ({ className, condition, mode }) => {
	const { availableObjectList, selectionList, enumListObjectsShort, currentEnumList } = useAppSelector(
		(state) => state.stepThree,
	);
	const [isFocus, setIsFocus] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>("");
	const selectRef = useRef<HTMLDivElement | null>(null);
	const dispatch = useAppDispatch();

	useEffect(() => {
		return () => {
			dispatch(ChangeChooseAll({ flag: null, mode: "" }));
		};
		//eslint-disable-next-line
	}, []);

	const hideMenu = () => {
		setIsFocus(false);
	};

	useOutsideAlerter(selectRef.current, hideMenu);

	const [availableList, enumList] = useMemo(() => {
		const availbleList = availableObjectList.filter((object) =>
			trimString(object.name).includes(trimString(searchValue)),
		);

		const enumList = enumListObjectsShort?.filter((object) =>
			trimString(object.obj.name).includes(trimString(searchValue)),
		);
		return [availbleList, enumList];
		//eslint-disable-next-line
	}, [availableObjectList, currentEnumList?.id, enumListObjectsShort, searchValue]);

	const chooseAllHandler = useCallback(() => {
		const arr = mode === "changing" ? [...availableList, ...enumList] : [...availableList];
		dispatch(ChangeChooseAll({ flag: true, mode, arr }));
		//eslint-disable-next-line
	}, [availableList, enumList]);

	return (
		<div ref={selectRef} className={cn(className, styles["select-wrp"])}>
			<span
				className={cn(
					condition && "error-input-title",
					styles.title,
					isFocus ? styles["title-focused"] : styles["title-unfocused"],
				)}>
				Выбрать элементы
				<span className="red"> *</span>
			</span>
			<div className={styles["input-wrp"]}>
				<input
					onClick={() => setIsFocus((prev) => !prev)}
					className={cn(styles.input, isFocus && styles["input-focused"], condition && "error-input")}
					type="text"
					value={searchValue}
					onChange={(e) => setSearchValue(e.target.value)}
					placeholder={`Выбрано ${selectionList.length} ${declOfLessonsNum(selectionList.length, [
						"элемент",
						"элемента",
						"элементов",
					])} `}
				/>
				<TickIcon className={cn(styles["input-icon"])} />
			</div>
			{isFocus && (
				<div className={styles.select}>
					<div className={styles.hud}>
						<span onClick={chooseAllHandler}>Выбрать всё</span>
						<span onClick={() => dispatch(ChangeChooseAll({ flag: false, mode }))}>Отменить выбор</span>
					</div>

					<ul className={cn(styles.list)}>
						{(searchValue ? availableList : availableObjectList).length ||
						(mode === "changing" && (searchValue ? enumList : enumListObjectsShort).length) ? (
							<>
								{mode === "changing" &&
									currentEnumList &&
									(searchValue ? enumList : enumListObjectsShort)?.map((object) => (
										<SelectObjectListItem key={object.obj.id} object={{ objectId: object.id, name: object.obj.name }} />
									))}
								{(searchValue ? availableList : availableObjectList).map((object) => (
									<SelectObjectListItem
										key={object.id}
										object={{
											objectId: object.id,
											objectTypeId: object?.objectType?.id,
											name: object.name,
											reselection: object.reselection,
										}}
									/>
								))}
							</>
						) : (
							<span className={styles["list-warning"]}>Нет доступных объектов</span>
						)}
					</ul>
				</div>
			)}
		</div>
	);
};
